import { useState, useEffect } from "react";
import { useAuth, axiosInstance } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { 
  Trophy, Users, MessageCircle, Calendar, Bell, Camera,
  Check, Loader2, ChevronRight, Minus, Plus, AlertCircle,
  ExternalLink, RefreshCw, Link2, Unlink, ShieldCheck, Settings, X
} from "lucide-react";
import { toast } from "sonner";

const SQUADLINE_LOGO = "https://customer-assets.emergentagent.com/job_c9359842-80d8-4acf-8a11-6884ae51c080/artifacts/7gldz4nd_Squadline%20logo.png";

const PRICING_TIERS = [
  { min: 1, max: 9, price: 14.99, discount: 0, label: "1-9 teams" },
  { min: 10, max: 24, price: 12.50, discount: 17, label: "10-24 teams" },
  { min: 25, max: 49, price: 11.96, discount: 20, label: "25-49 teams" },
  { min: 50, max: Infinity, price: 9.98, discount: 33, label: "50+ teams" },
];

const getTierForQuantity = (qty) => {
  for (const tier of PRICING_TIERS) {
    if (qty >= tier.min && qty <= tier.max) return tier;
  }
  return PRICING_TIERS[PRICING_TIERS.length - 1];
};

export default function SquadlineIntegration({ organization, marketplaceApp }) {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [providerStatus, setProviderStatus] = useState(null);
  const [providerError, setProviderError] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [syncing, setSyncing] = useState(null);
  
  // Modal states
  const [showManageModal, setShowManageModal] = useState(false);
  const [manageView, setManageView] = useState('overview'); // 'overview', 'purchase', 'teams'
  const [quantity, setQuantity] = useState(1);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [billingInterval, setBillingInterval] = useState('month'); // 'month' or 'year'
  const [pricing, setPricing] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [syncingSeats, setSyncingSeats] = useState(false);
  const [launching, setLaunching] = useState(false);

  useEffect(() => {
    if (organization?.org_id && token) {
      loadStatus();
      loadProviderStatus();
      loadTeams();
    }
  }, [organization?.org_id, token]);

  // Load pricing when quantity changes
  useEffect(() => {
    if (status?.connected && manageView === 'purchase') {
      loadPricing();
    }
  }, [quantity, status?.connected, manageView]);

  // Handle URL params from OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const squadlineStatus = params.get('squadline');
    const squadlineError = params.get('squadline_error');
    const sessionId = params.get('session_id');
    
    if (squadlineStatus === 'connected') {
      toast.success('Successfully connected to Squadline!');
      loadStatus();
      window.history.replaceState({}, '', window.location.pathname + '?tab=integrations');
    } else if (squadlineStatus === 'success') {
      // Checkout return is not proof of entitlement; the signed webhook is authoritative.
      toast.success('Checkout completed. Squadline access will activate after payment confirmation.');
      loadStatus();
      loadTeams();
      setShowManageModal(true);
      setManageView('overview');
      window.history.replaceState({}, '', window.location.pathname + '?tab=integrations');
    } else if (squadlineStatus === 'purchase_success' && sessionId) {
      confirmPurchase(sessionId);
      window.history.replaceState({}, '', window.location.pathname + '?tab=integrations');
    } else if (squadlineStatus === 'cancelled') {
      toast.info('Subscription checkout was cancelled');
      window.history.replaceState({}, '', window.location.pathname + '?tab=integrations');
    } else if (squadlineStatus === 'purchase_cancelled') {
      toast.error('Purchase cancelled');
      window.history.replaceState({}, '', window.location.pathname + '?tab=integrations');
    } else if (squadlineError) {
      const errorMessages = {
        'invalid_state': 'Connection failed: Invalid state token. Please try again.',
        'expired_state': 'Connection failed: Authorization expired. Please try again.',
        'token_exchange_failed': 'Connection failed: Could not complete authorization.',
        'server_error': 'Connection failed: Server error. Please try again later.',
        'access_denied': 'Connection cancelled by user.'
      };
      toast.error(errorMessages[squadlineError] || `Connection error: ${squadlineError}`);
      window.history.replaceState({}, '', window.location.pathname + '?tab=integrations');
    }
  }, []);

  const loadStatus = async () => {
    try {
      const response = await axiosInstance.get(`/squadline/status/${organization.org_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
      
      if (response.data.teams_count > 0) {
        setQuantity(response.data.teams_count);
      }
    } catch (error) {
      console.error("Error loading Squadline status:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadProviderStatus = async () => {
    try {
      setProviderError(null);
      const response = await axiosInstance.get(`/squadline/v1/status/${organization.org_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProviderStatus(response.data);
    } catch (error) {
      if (error.response?.status !== 404) setProviderError('Could not verify the latest Squadline entitlement.');
      setProviderStatus(null);
    }
  };

  const handleProviderLaunch = async () => {
    try {
      setLaunching(true);
      const response = await axiosInstance.post(`/squadline/v1/launch/${organization.org_id}`, {
        audience: 'squadline-mobile'
      }, { headers: { Authorization: `Bearer ${token}` } });
      const launchUrl = new URL('https://app.squadline.com/sporthouse-launch');
      launchUrl.searchParams.set('code', response.data.code);
      window.location.assign(launchUrl.toString());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create a secure Squadline launch.');
      setLaunching(false);
    }
  };

  const loadTeams = async () => {
    try {
      const response = await axiosInstance.get(`/squadline/teams/${organization.org_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data);
    } catch (error) {
      console.error("Error loading teams:", error);
    }
  };

  const loadPricing = async () => {
    try {
      setLoadingPricing(true);
      const response = await axiosInstance.get(`/squadline/billing/pricing?quantity=${quantity}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPricing(response.data);
    } catch (error) {
      console.error("Error loading pricing:", error);
      // Use fallback pricing calculation
      const monthlyPerSeat = quantity >= 100 ? 8.99 : quantity >= 50 ? 9.98 : quantity >= 25 ? 11.96 : quantity >= 10 ? 12.50 : 14.99;
      const monthlyTotal = monthlyPerSeat * quantity;
      const annualTotal = monthlyTotal * 10;
      const annualSavings = (monthlyTotal * 12) - annualTotal;
      
      setPricing({
        quantity,
        monthly: { per_seat: monthlyPerSeat, total: monthlyTotal, interval: 'month' },
        annual: { 
          per_seat: (annualTotal / quantity / 12).toFixed(2), 
          total: annualTotal, 
          monthly_equivalent: (annualTotal / 12).toFixed(2),
          interval: 'year', 
          savings: annualSavings,
          free_months: 2
        },
        volume_discount_applied: quantity >= 10
      });
    } finally {
      setLoadingPricing(false);
    }
  };

  const handleSyncSeats = async () => {
    try {
      setSyncingSeats(true);
      const response = await axiosInstance.post('/squadline/billing/seats/sync', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`Synced: ${response.data.seats_purchased} licenses, ${response.data.seats_available} available`);
        loadStatus(); // Refresh the status to show updated seats
      }
    } catch (error) {
      console.error("Error syncing seats:", error);
      toast.error(error.response?.data?.detail || 'Failed to sync seats from Squadline');
    } finally {
      setSyncingSeats(false);
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      // New multi-tenant flow: POST to register org and get credentials
      const response = await axiosInstance.post(`/squadline/connect/${organization.org_id}`, {
        billing_email: organization.contact_email || organization.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message || 'Successfully connected to Squadline!');
        loadStatus();
        loadTeams();
      } else if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      console.error("Error initiating connection:", error);
      toast.error(error.response?.data?.detail || "Failed to connect to Squadline");
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Are you sure you want to disconnect from Squadline? This will disable all team integrations.")) {
      return;
    }
    
    try {
      setDisconnecting(true);
      await axiosInstance.post(`/squadline/disconnect/${organization.org_id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Disconnected from Squadline");
      setShowManageModal(false);
      loadStatus();
      loadTeams();
    } catch (error) {
      toast.error("Failed to disconnect");
    } finally {
      setDisconnecting(false);
    }
  };

  const confirmPurchase = async (sessionId) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(`/squadline/confirm-purchase?session_id=${sessionId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setManageView('teams');
        setShowManageModal(true);
        loadStatus();
        loadTeams();
      }
    } catch (error) {
      console.error("Error confirming purchase:", error);
      toast.error("Failed to confirm purchase");
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    try {
      setPurchasing(true);
      const response = await axiosInstance.post('/squadline/billing/subscription/create', {
        quantity,
        billing_interval: billingInterval
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Squadline's Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to get checkout URL');
        setPurchasing(false);
      }
    } catch (error) {
      console.error("Error starting purchase:", error);
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setPurchasing(false);
    }
  };

  const handleEnableTeams = async () => {
    if (selectedTeams.length === 0) {
      toast.error("Please select at least one team");
      return;
    }
    
    if (selectedTeams.length > status.available_seats) {
      toast.error(`You only have ${status.available_seats} available license(s)`);
      return;
    }
    
    try {
      setEnabling(true);
      const response = await axiosInstance.post(`/squadline/enable-teams/${organization.org_id}`, {
        team_ids: selectedTeams
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setSelectedTeams([]);
        setManageView('overview');
        loadStatus();
        loadTeams();
      }
    } catch (error) {
      console.error("Error enabling teams:", error);
      toast.error(error.response?.data?.detail || "Failed to enable teams");
    } finally {
      setEnabling(false);
    }
  };

  const handleDisableTeam = async (teamId) => {
    if (!window.confirm("Are you sure you want to disable Squadline for this team?")) return;
    
    try {
      await axiosInstance.post(`/squadline/disable-team/${teamId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Team disabled from Squadline");
      loadStatus();
      loadTeams();
    } catch (error) {
      toast.error("Failed to disable team");
    }
  };

  const handleSyncTeam = async (teamId) => {
    try {
      setSyncing(teamId);
      const response = await axiosInstance.post(`/squadline/sync-team/${teamId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message || "Team synced successfully");
        loadTeams();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync team");
    } finally {
      setSyncing(null);
    }
  };

  const toggleTeamSelection = (teamId) => {
    setSelectedTeams(prev => 
      prev.includes(teamId) 
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };

  const currentTier = getTierForQuantity(quantity);
  const totalPrice = (quantity * currentTier.price).toFixed(2);
  const enabledTeams = teams.filter(t => t.squadline_enabled);
  const unenabledTeams = teams.filter(t => !t.squadline_enabled);

  // Render the simple row (like other integrations)
  return (
    <>
      {/* Simple Integration Row */}
      <div className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-xl" data-testid="squadline-integration">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-white border border-slate-200 rounded-lg flex items-center justify-center overflow-hidden shadow-sm" style={{ backgroundColor: '#ffffff' }}>
            <img 
              src={SQUADLINE_LOGO} 
              alt="Squadline" 
              className="h-7 w-7 object-contain"
              style={{ maxWidth: '28px', maxHeight: '28px' }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<span style="color: black; font-size: 18px; font-weight: bold;">S</span>';
              }}
            />
          </div>
          <div>
            <h4 className="font-medium text-slate-900">Squadline</h4>
            <p className="text-sm text-slate-500">
              {marketplaceApp?.description || 'Team communication & scheduling app'}
            </p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-slate-500">{marketplaceApp?.pricing_model || 'Per enabled team'}</span>
              {status?.installation_status && status.installation_status !== 'available' && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  status.installation_status === 'active'
                    ? 'bg-green-100 text-green-700'
                    : status.installation_status === 'suspended' || status.installation_status === 'degraded'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-amber-100 text-amber-700'
                }`}>
                  {status.installation_status.replace('_', ' ')}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          ) : status?.connected ? (
            <>
              <span className="text-sm text-green-600 font-medium mr-2">Connected</span>
              {providerStatus && ['active', 'grace'].includes(providerStatus.entitlement_status) && (
                <Button size="sm" onClick={handleProviderLaunch} disabled={launching} data-testid="squadline-launch-btn">
                  {launching ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ExternalLink className="w-4 h-4 mr-1" />Open Squadline</>}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setManageView('overview');
                  setShowManageModal(true);
                }}
                data-testid="squadline-manage-btn"
              >
                <Settings className="w-4 h-4 mr-1" />
                Manage
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDisconnect}
                disabled={disconnecting}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="squadline-disconnect-btn"
              >
                {disconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
              </Button>
            </>
          ) : (
            <Button
              onClick={handleConnect}
              disabled={connecting}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="squadline-connect-btn"
            >
              {connecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Installing...
                </>
              ) : (
                <>
                  <Link2 className="w-4 h-4 mr-2" />
                  Install
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Management Modal */}
      <Dialog open={showManageModal} onOpenChange={setShowManageModal}>
        <DialogContent className="sm:max-w-3xl max-h-[95vh] flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white border border-slate-200 rounded-lg flex items-center justify-center shadow-sm">
                <img 
                  src={SQUADLINE_LOGO} 
                  alt="Squadline" 
                  className="h-7 w-7 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<span class="text-black text-lg font-bold">S</span>';
                  }}
                />
              </div>
              <div>
                <DialogTitle>Squadline Integration</DialogTitle>
                <DialogDescription>Manage your team communication & scheduling</DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-4">
            {/* Navigation Tabs */}
            <div className="flex gap-2 mb-6 border-b border-slate-200 pb-3">
              <Button
                variant={manageView === 'overview' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setManageView('overview')}
                className={manageView === 'overview' ? 'bg-slate-900' : ''}
              >
                Overview
              </Button>
              {!providerStatus && <Button variant={manageView === 'purchase' ? 'default' : 'ghost'} size="sm" onClick={() => setManageView('purchase')} className={manageView === 'purchase' ? 'bg-slate-900' : ''}>Purchase Licenses</Button>}
              {status?.available_seats > 0 && (
                <Button
                  variant={manageView === 'teams' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setManageView('teams')}
                  className={manageView === 'teams' ? 'bg-slate-900' : ''}
                >
                  Enable Teams
                </Button>
              )}
            </div>

            {/* Overview View */}
            {manageView === 'overview' && (
              <div className="space-y-6">
                {providerStatus && (
                  <div className={`rounded-xl border p-4 ${providerStatus.entitlement_status === 'active' ? 'border-green-200 bg-green-50' : providerStatus.entitlement_status === 'grace' ? 'border-amber-200 bg-amber-50' : 'border-red-200 bg-red-50'}`}>
                    <p className="font-medium text-slate-900">Sporthouse-managed access: {providerStatus.entitlement_status}</p>
                    <p className="mt-1 text-sm text-slate-600">
                      {providerStatus.entitlement_status === 'active' && 'Billing and seat entitlement are managed in Sporthouse.'}
                      {providerStatus.entitlement_status === 'grace' && 'Access remains available during the grace period. Review Sporthouse billing to prevent interruption.'}
                      {providerStatus.entitlement_status === 'pending' && 'Provisioning or payment confirmation is still pending. Refresh for the latest state.'}
                      {providerStatus.entitlement_status === 'suspended' && 'Squadline launch is paused until the Sporthouse entitlement is restored.'}
                      {providerStatus.entitlement_status === 'cancelled' && 'Access has ended. Existing Squadline data is retained; reactivate through Sporthouse.'}
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={loadProviderStatus}><RefreshCw className="mr-1 h-4 w-4" />Refresh status</Button>
                      {['active', 'grace'].includes(providerStatus.entitlement_status) && <Button size="sm" onClick={handleProviderLaunch} disabled={launching}><ExternalLink className="mr-1 h-4 w-4" />Open Squadline</Button>}
                    </div>
                  </div>
                )}
                {providerError && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">{providerError} Cached state is not being used as proof of access.</div>}
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-slate-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-slate-900">{status?.teams_count || 0}</p>
                    <p className="text-sm text-slate-500">Teams in Org</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-amber-600">{status?.seats_purchased || 0}</p>
                    <p className="text-sm text-slate-500">Licenses Purchased</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{status?.enabled_teams || 0}</p>
                    <p className="text-sm text-slate-500">Teams Enabled</p>
                  </div>
                </div>

                {/* Sync Seats Button */}
                <div className="flex justify-end">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleSyncSeats}
                    disabled={syncingSeats}
                    className="text-slate-600"
                  >
                    {syncingSeats ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Syncing...</>
                    ) : (
                      <><RefreshCw className="w-4 h-4 mr-2" />Sync Seats</>
                    )}
                  </Button>
                </div>

                {/* Available Licenses */}
                {status?.available_seats > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-blue-900">
                          You have {status.available_seats} unused license(s)
                        </p>
                        <p className="text-sm text-blue-700">
                          Enable teams to give coaches and parents access to Squadline
                        </p>
                      </div>
                      <Button 
                        onClick={() => setManageView('teams')}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Enable Teams
                      </Button>
                    </div>
                  </div>
                )}

                {/* No licenses warning */}
                {status?.seats_purchased === 0 && !providerStatus && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                      <div className="flex-1">
                        <p className="font-medium text-amber-900">No team licenses yet</p>
                        <p className="text-sm text-amber-700">
                          Purchase licenses to give your coaches and parents access to the Squadline app
                        </p>
                      </div>
                      <Button 
                        onClick={() => setManageView('purchase')}
                        className="bg-amber-600 hover:bg-amber-700"
                      >
                        Purchase
                      </Button>
                    </div>
                  </div>
                )}

                {/* Enabled Teams List */}
                {enabledTeams.length > 0 && (
                  <div>
                    <h4 className="font-medium text-slate-900 mb-3">Enabled Teams ({enabledTeams.length})</h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {enabledTeams.map(team => (
                        <div 
                          key={team.team_id}
                          className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <Check className="w-5 h-5 text-green-600" />
                            <div>
                              <p className="font-medium text-slate-900">{team.name}</p>
                              <p className="text-xs text-slate-500">
                                {team.player_count || 0} players
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSyncTeam(team.team_id)}
                              disabled={syncing === team.team_id}
                            >
                              {syncing === team.team_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDisableTeam(team.team_id)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Features */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-sm font-medium text-slate-700 mb-3">Squadline Features</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <MessageCircle className="w-4 h-4 text-amber-500" />
                      <span>Team Chat</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Calendar className="w-4 h-4 text-amber-500" />
                      <span>Event RSVP</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Bell className="w-4 h-4 text-amber-500" />
                      <span>Push Notifications</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Camera className="w-4 h-4 text-amber-500" />
                      <span>Photo Sharing</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Purchase View */}
            {manageView === 'purchase' && (
              <div className="space-y-4">
                {/* Quantity and Billing in a row */}
                <div className="flex items-start gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Team licenses
                    </label>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        disabled={quantity <= 1}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <Input
                        type="number"
                        min={1}
                        max={500}
                        value={quantity}
                        onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-20 text-center text-lg font-semibold"
                      />
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => setQuantity(quantity + 1)}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Billing Interval Toggle */}
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Billing Frequency
                    </label>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setBillingInterval('month')}
                        className={`flex-1 p-3 rounded-xl border-2 transition-all ${
                          billingInterval === 'month'
                            ? 'border-amber-400 bg-amber-50'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        <p className="font-semibold text-slate-900">Monthly</p>
                        {pricing && (
                          <p className="text-lg font-bold text-amber-600">
                            ${pricing.monthly?.total?.toFixed(2) || (currentTier.price * quantity).toFixed(2)}
                            <span className="text-sm font-normal text-slate-500">/mo</span>
                          </p>
                        )}
                        <p className="text-xs text-slate-500">Cancel anytime</p>
                      </button>
                      <button
                        onClick={() => setBillingInterval('year')}
                        className={`flex-1 p-3 rounded-xl border-2 transition-all relative ${
                          billingInterval === 'year'
                            ? 'border-green-400 bg-green-50'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        {pricing?.annual?.savings > 0 && (
                          <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                            Save ${pricing.annual.savings.toFixed(0)}
                          </span>
                        )}
                        <p className="font-semibold text-slate-900">Annual</p>
                        {pricing && (
                          <>
                            <p className="text-lg font-bold text-green-600">
                              ${typeof pricing.annual?.monthly_equivalent === 'number' 
                                ? pricing.annual.monthly_equivalent.toFixed(2) 
                                : pricing.annual?.monthly_equivalent || (pricing.annual?.total / 12).toFixed(2)}
                              <span className="text-sm font-normal text-slate-500">/mo</span>
                            </p>
                            <p className="text-xs text-green-600 font-medium">
                              2 months FREE
                            </p>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Volume Discount Tiers - Horizontal */}
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-sm font-medium text-slate-700 mb-2">Volume Discounts</p>
                  <div className="grid grid-cols-4 gap-2">
                    {PRICING_TIERS.map((tier, idx) => (
                      <div 
                        key={idx}
                        className={`p-2 rounded-lg text-center ${
                          currentTier === tier 
                            ? 'bg-amber-100 border border-amber-300' 
                            : 'bg-white border border-slate-200'
                        }`}
                      >
                        <p className="text-xs font-medium text-slate-900">{tier.label}</p>
                        <p className="text-sm text-amber-600 font-semibold">${tier.price}/team</p>
                        {tier.discount > 0 && (
                          <p className="text-xs text-green-600">{tier.discount}% off</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Total */}
                <div className="bg-slate-900 text-white rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-300 text-sm">
                        {quantity} team license{quantity > 1 ? 's' : ''} - {billingInterval === 'year' ? 'Annual' : 'Monthly'}
                      </p>
                      {billingInterval === 'month' ? (
                        <p className="text-2xl font-bold">
                          ${pricing?.monthly?.total?.toFixed(2) || totalPrice}
                          <span className="text-sm font-normal text-slate-400">/month</span>
                        </p>
                      ) : (
                        <div>
                          <p className="text-2xl font-bold">
                            ${pricing?.annual?.total?.toFixed(2) || (parseFloat(totalPrice) * 10).toFixed(2)}
                            <span className="text-sm font-normal text-slate-400">/year</span>
                          </p>
                          <p className="text-sm text-green-400">
                            = ${pricing?.annual?.monthly_equivalent || (parseFloat(totalPrice) * 10 / 12).toFixed(2)}/mo
                          </p>
                        </div>
                      )}
                    </div>
                    <Button 
                      onClick={handlePurchase}
                      disabled={purchasing || loadingPricing}
                      className="bg-amber-500 hover:bg-amber-600 text-white"
                    >
                      {purchasing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <ShieldCheck className="w-4 h-4 mr-2" />
                          Subscribe
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    {billingInterval === 'year' 
                      ? 'Billed annually. Volume discounts + annual savings applied.' 
                      : 'Billed monthly. Cancel anytime.'}
                  </p>
                </div>
              </div>
            )}

            {/* Team Selection View */}
            {manageView === 'teams' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-600">
                    Select teams to enable ({selectedTeams.length} selected, {status?.available_seats || 0} licenses available)
                  </p>
                </div>

                {unenabledTeams.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-xl">
                    <Trophy className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">All teams are already enabled!</p>
                    <Button 
                      variant="outline" 
                      className="mt-3"
                      onClick={() => setManageView('overview')}
                    >
                      Back to Overview
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {unenabledTeams.map(team => (
                        <div
                          key={team.team_id}
                          onClick={() => toggleTeamSelection(team.team_id)}
                          className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                            selectedTeams.includes(team.team_id)
                              ? 'bg-amber-50 border-2 border-amber-400'
                              : 'bg-slate-50 border-2 border-transparent hover:bg-slate-100'
                          }`}
                        >
                          <Checkbox 
                            checked={selectedTeams.includes(team.team_id)}
                            onCheckedChange={() => toggleTeamSelection(team.team_id)}
                          />
                          <div className="flex-1">
                            <p className="font-medium text-slate-900">{team.name}</p>
                            <p className="text-sm text-slate-500">
                              {team.sport} {team.division && `• ${team.division}`}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <Button 
                      onClick={handleEnableTeams}
                      disabled={enabling || selectedTeams.length === 0 || selectedTeams.length > (status?.available_seats || 0)}
                      className="w-full bg-amber-600 hover:bg-amber-700"
                    >
                      {enabling ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Enabling...
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Enable {selectedTeams.length} Team(s)
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="flex-shrink-0 border-t pt-4">
            <Button variant="outline" onClick={() => setShowManageModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
